import React, { FC, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { fetchSettingCommon } from '../../../../states/containers/setting/systemSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { URL } from '../../../constants/url'
import { useHistory, useLocation } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { responseGetSettingCommon, RolePermission, settingCommonSettingDetail, settingCommonTaskDetail } from '../../../../states/types/setting'
import LoadingSquare from '../../../components/common/LoadingSquare'

const COMMON_REQUIRED_LIST = [
  {
    key: "member_setting",
    title: "ユーザー設定",
    description: "Qaseeを利用するユーザーを設定します。",
    url: URL.SETTING_MEMBER.path,
  },
  {
    key: "team_setting",
    title: "チーム設定",
    description: "ユーザーが所属するチームを設定します。",
    url: URL.SETTING_TEAM.path,
  },
  {
    key: "member_import",
    title: "ユーザーCSV一括インポート",
    description: "ユーザー情報をCSVで一括インポートします。",
    url: URL.SETTING_IMPORT_CSV.path,
  },
]

const COMMON_COMPANY_LIST = [
  {
    key: "ip_restriction_setting",
    title: "IPアドレス制限設定",
    description: "アクセスできるIPアドレスを制限して、セキュリティを強化できます。",
    url: URL.SETTING_IP_RESTRICTION.path,
  },
  {
    key: "office_ip_address_setting",
    title: "オフィスのIPアドレス設定",
    description: "アクセス元によるパフォーマンス差異を計測できます。",
    url: URL.SETTING_OFFICE_IP_ADDRESS.path,
  },
  {
    key: "language_setting",
    title: "言語設定（配信メール・通知）",
    description: "Qaseeからの配信されるメールや通知で表示される言語を設定します。",
    url: URL.SETTING_LANGUAGE.path,
  },
  {
    key: "attendance_integration_setting",
    title: "勤怠データ連携設定",
    description: "ユーザーの勤怠データ連携を設定できます。",
    url: URL.SETTING_RECODE_API_CONNECT.path,
  },
  {
    key: "approval_group_setting",
    title: "承認グループ設定",
    description: "ユーザーの承認フローを設定します。",
    url: URL.SETTING_TASK_APPROVAL_SETTING.path,
  },
]

const COMMON_OPERATION_LIST = [
  {
    key: "measurement_setting",
    title: "計測時間設定",
    description: "Qaseeで計測する時間を設定できます。",
    url: URL.SETTING_MEASUREMENT.path,
  },
  {
    key: "base_work_time_setting",
    title: "標準勤務時刻・休憩時刻設定",
    description: "勤務開始・終了時間、休憩開始・終了時間が設定できます。",
    url: URL.SETTING_BASE_WORK_TIME.path,
  },
  {
    key: "holiday_setting",
    title: "休日設定",
    description: "ユーザーごとに休日が設定できます。",
    url: URL.SETTING_HOLIDAY.path,
  },
  {
    key: "measure_restriction_setting",
    title: "計測停止の制限設定",
    description: "ユーザーによる計測停止の制限を設定できます。",
    url: URL.SETTING_MEASUREMENT_RESTRICTION.path,
  },
  {
    key: "web_browser_setting",
    title: "Webブラウザの詳細非表示設定",
    description: "URLとページタイトルを非表示にできます。",
    url: URL.SETTING_WEB_BROWSER.path,
  },
  {
    key: "threshold_time_setting",
    title: "業務仕分けボードに表示する作業データの最小時間",
    description: "業務仕分けボードに表示する作業データの最小時間を設定します。",
    url: URL.SETTING_TASK_THRESHOLD_TIME_SETTING.path,
  },
  {
    key: "create_task_by_url_setting",
    title: "業務仕分けボードの作業データをURLごとに作成する",
    description: "業務仕分けボードの作業データをURLごとに作成するようにします。",
    url: URL.SETTING_TASK_CREATE_TASK_BY_URL_SETTING.path,
  },
  {
    key: "exclude_task_generate_member_setting",
    title: "作業データの非自動生成設定",
    description: "作業データの非自動生成を設定します。",
    url: URL.SETTING_TASK_EXCLUDE_GENERATE_MEMBER_SETTING.path,
  },
  {
    key: "learning_scope_setting",
    title: "仕分け提案に使用する過去データの範囲設定",
    description: "作業データの仕分け提案に使用する過去データの範囲を設定します。",
    url: URL.SETTING_TASK_LEARNING_SCOPE_SETTING.path,
  },
  {
    key: "create_online_meeting_task_setting",
    title: "オンラインミーティング時の作業データ設定",
    description: "オンラインミーティング時、操作等がない時でも作業データを自動作成するように設定します。",
    url: URL.SETTING_TASK_ONLINE_MEETING_SETTING.path,
  },
]

type CommonSettingKeys = keyof settingCommonSettingDetail
type CommonTaskKeys = keyof settingCommonTaskDetail

const Index: FC = () => {
  const dispatch = useDispatch()
  const { login, commonPart } = useSelector<RootState, RootState>((state: RootState) => (state))
  const history = useHistory()
  const { hash } = useLocation()

  const [settingCommon, setSettingCommon] = useState<responseGetSettingCommon>()
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const scrollToAnchor = () => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''))
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const buildCard = (data: { key: string, title: string, description: string, url: string, check: boolean }) => {
    const controllable = login.role_permission ? login.role_permission[data.key as keyof RolePermission] : false
    const dom = (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            userSelect: "none",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "40px",
              height: "40px",
              backgroundColor: data.check ? "#487fce" : "#FFFFFF",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontSize: "22px",
              padding: "8px",
            }}
          >
            <img
              src="/static/images/logo_q.png"
              alt=""
              style={{
                width: "auto",
                height: "100%",
                filter: data.check ? "saturate(0%) brightness(2)" : "saturate(0%) brightness(1.5)",
              }}
            />
            {
              !data.check && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "-5px",
                    color: "#999999",
                    fontSize: "10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  未設定
                </div>
              )
            }
          </div>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "4px",
            }}
          >
            <div
              style={{
                fontSize: "18px",
              }}
            >
              {data.title}
            </div>
            <div>
              {data.description}
            </div>
          </div>
        </div>
      </>
    )
    if (controllable) {
      return (
        <button
          type="button"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "4px",
            border: "solid 1px #CCCCCC",
            borderRadius: "4px",
            padding: "16px",
            width: "calc(50% - 8px)",
            backgroundColor: "#EEEEEE",
          }}
          onClick={() => history.push(setPath(data.url, "ja"))}
        >
          {dom}
          <FontAwesomeIcon icon={['fas', 'angle-right']} />
        </button>
      )
    }
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "4px",
          border: "solid 1px #CCCCCC",
          borderRadius: "4px",
          padding: "16px",
          width: "calc(50% - 8px)",
          backgroundColor: "#EEEEEE",
          cursor: "not-allowed",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999999",
            fontSize: "18px",
            fontWeight: "bold",
            borderRadius: "4px",
          }}
        >
          操作する権限がありません
        </div>
        {dom}
      </div>
    )
  }

  useEffect(() => {
    if (!login.accessToken) return
    setIsLoading(true)
    dispatch(
      fetchSettingCommon(
        (result) => {
          setSettingCommon(result)
          setIsLoading(false)
        },
      ),
    )
  }, [login.accessToken])

  useEffect(() => {
    if (isLoading) return
    setTimeout(() => {
      scrollToAnchor()
    }, 500)
  }, [isLoading])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </h1>
        {
          !!login.role_name && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                }}
              >
                現在のロール
              </div>
              <div
                style={{
                  fontSize: "10px",
                  backgroundColor: "#487fce",
                  color: "#FFFFFF",
                  padding: "4px 8px",
                  borderRadius: "20px",
                }}
              >
                {login.role_name}
              </div>
            </div>
          )
        }
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="block-header">
              <div className="title">
                必須設定
              </div>
            </div>
            <div className="block-detail">
              {
                isLoading ? (
                  <>
                    <LoadingSquare>
                      <>
                        情報を取得しています…
                      </>
                    </LoadingSquare>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      {
                        COMMON_REQUIRED_LIST.map((data) => (
                          <>
                            {
                              buildCard({
                                ...data,
                                check: settingCommon?.setting[data.key as CommonSettingKeys] ?? true,
                              })
                            }
                          </>
                        ))
                      }
                    </div>
                  </>
                )
              }
            </div>
          </div>
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="block-header">
              <div className="title">
                全社設定
              </div>
            </div>
            <div className="block-detail">
              {
                isLoading ? (
                  <>
                    <LoadingSquare>
                      <>
                        情報を取得しています…
                      </>
                    </LoadingSquare>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      {
                        COMMON_COMPANY_LIST.map((data) => (
                          <>
                            {
                              buildCard({
                                ...data,
                                check: settingCommon?.setting[data.key as CommonSettingKeys] ?? true,
                              })
                            }
                          </>
                        ))
                      }
                      {
                        (commonPart.payment_finished && login.feature_config?.change_contract) && (
                          <>
                            {
                              buildCard({
                                key: "contract_setting",
                                title: "契約情報設定",
                                description: "契約オプションの追加やユーザー上限の追加が設定できます。",
                                url: URL.SETTING_CONTRACT.path,
                                check: true,
                              })
                            }
                          </>
                        )
                      }
                      {
                        login.feature_config?.external_api && (
                          <>
                            {
                              buildCard({
                                key: "external_api_key_setting",
                                title: "API設定",
                                description: "API接続用アクセスキーの設定ができます。",
                                url: URL.SETTING_EXTERNAL_API_KEY.path,
                                check: settingCommon?.setting.external_api_key_setting ?? false,
                              })
                            }
                          </>
                        )
                      }
                    </div>
                  </>
                )
              }
            </div>
          </div>
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="block-header">
              <div className="title">
                個別設定
              </div>
            </div>
            <div className="block-detail">
              {
                isLoading ? (
                  <>
                    <LoadingSquare>
                      <>
                        情報を取得しています…
                      </>
                    </LoadingSquare>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      {
                        COMMON_OPERATION_LIST.map((data) => (
                          <>
                            {
                              buildCard({
                                ...data,
                                check: settingCommon?.setting[data.key as CommonSettingKeys] ?? true,
                              })
                            }
                          </>
                        ))
                      }
                    </div>
                  </>
                )
              }
            </div>
          </div>
        </div>
      </section>
    </section>
  )
}

export default Index
